import { computed, Injectable, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { lastValueFrom } from "rxjs";
import { Language, PaymentLanguage } from "../common/languages.config";

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly langStorageKey = 'selectedLangUlazniceNogomet';
  private readonly supportedLanguages: Language[] = [Language.HR, Language.EN];
  private readonly defaultLang = Language.HR;

  private readonly languageInUse = signal<Language>(this.getFromLocalStorage());
  public currentLanguage = computed(() => this.languageInUse());

  private readonly paymentLangMap: Record<Language, PaymentLanguage> = {
    [Language.HR]: PaymentLanguage.HRV,
    [Language.EN]: PaymentLanguage.ENG,
  };

  public readonly paymentLanguage = computed<PaymentLanguage>(() => {
    const lang = this.languageInUse();
    return this.paymentLangMap[lang] ?? PaymentLanguage.HRV;
  });

  constructor(private readonly translate: TranslateService) {}

  async initAsync(): Promise<void> {
    this.translate.addLangs(this.supportedLanguages);
    const storedLang = localStorage.getItem(this.langStorageKey);

    const finalLang = (storedLang && this.isSupported(storedLang as Language))
      ? storedLang as Language
      : this.defaultLang;

    localStorage.setItem(this.langStorageKey, finalLang);
    this.languageInUse.set(finalLang);
    this.translate.setDefaultLang(finalLang);

    try {
      await lastValueFrom(this.translate.use(finalLang));
    } catch (error) {
      if (finalLang !== this.defaultLang) {
        try {
          await lastValueFrom(this.translate.use(this.defaultLang));
          this.languageInUse.set(this.defaultLang);
        } catch (defaultError) {
        }
      }
    }
  }

  switchLanguage(lang: Language): void {
    if (this.isSupported(lang)) {
      localStorage.setItem(this.langStorageKey, lang);
      this.translate.use(lang);
    } else {
      this.translate.use(this.defaultLang);
    }

    window.location.reload()
  }

  private getFromLocalStorage(): Language {
    return (localStorage.getItem(this.langStorageKey) as Language) || this.defaultLang;
  }

  private isSupported(lang: Language): boolean {
    return this.supportedLanguages.includes(lang);
  }
}
